import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { ScaleIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { updateStaffPayScale } from '../../api/staff-pay-scales/edit-staff-pay-scale'
import { getStaffPayScale } from '../../api/staff-pay-scales/get-staff-pay-scale'
import { ItemNotFound } from '../../components/elements/item-not-found'
import UserNavBar from '../../components/elements/nav-bar'
import StaffPayScaleForm from '../../components/pages/staff/staff-pay-scale-form/staff-pay-scale-form'
import { Paths } from '../../lib/constants/path'
import { StaffFilters } from '../../lib/types/models/staff/staff'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

export const staffInitialFilter: StaffFilters = {
  active: true
}

export function StaffPayScaleUpdatePage() {
  const { id: staffPayScaleId } = useParams()
  const { navigate, back } = useNavigator()
  const queryClient = useQueryClient()

  if (!staffPayScaleId || isNaN(parseInt(staffPayScaleId)))
    return <ItemNotFound title="Identifiant de barème invalide" />

  const { data, isLoading } = useQuery({
    queryKey: ['staff-pay-scale', staffPayScaleId],
    queryFn: () => getStaffPayScale(parseInt(staffPayScaleId))
  })

  const { mutateAsync } = useMutation({
    mutationFn: updateStaffPayScale,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({
        exact: true,
        queryKey: ['staff-pay-scale', staffPayScaleId]
      })
      toast.success('Barème de rémunération modifié avec succès')
    }
  })

  if (!data || isLoading)
    return (
      <div className="flex justify-center items-center h-full">
        {isLoading ? <Loader /> : <ItemNotFound title="Barème non trouvé" />}
      </div>
    )

  return (
    <div>
      <UserNavBar title="Modifier des barèmes de rémunération" icon={ScaleIcon} />
      <div className="h-4" />
      <Box>
        <Typography variant="sub-heading">Modifier des grilles de rémunération</Typography>
        <div className="h-4" />
        <StaffPayScaleForm
          defaultValues={{
            ...data,
            formulasId: data?.formulas.map((formula) => formula.id) || [],
            countriesId: data?.countries.map((country) => country.id) || [],
            periodsId: data?.periods.map((period) => period.id) || []
          }}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.STAFF_PAY_SCALE_LIST_PAGE),
            onUpdate: (values) => mutateAsync({ id: parseInt(staffPayScaleId), ...values }),
            onUpdateThenCreate: async (values) => {
              await mutateAsync({ id: parseInt(staffPayScaleId), ...values })
              navigate(Paths.STAFF_PAY_SCALE_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await mutateAsync({ id: parseInt(staffPayScaleId), ...values })
              back(Paths.STAFF_PAY_SCALE_LIST_PAGE)
            }
          })}
        />
      </Box>
    </div>
  )
}
